import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  OTHER_SERIES_KEY,
  buildBulkLostSalesTimeline,
  peakTimelineMonth,
} from "../../utils/lostSalesBulkTimeline";
import { formatCurrencyAmount } from "../../utils/lostSalesRevenue";
import { useDarkMode } from "../context/darkModeContext";

// Reference categorical slots 1–5 (fixed order, validated adjacent-pair CVD
// separation in both modes); "Other" stays neutral so it never reads as a SKU.
const SERIES_COLORS = {
  light: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4"],
  dark: ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181"],
};
const OTHER_COLOR = { light: "#a3a29c", dark: "#6f6e69" };

const formatUnits = (value) =>
  (Math.round((Number(value) || 0) * 10) / 10).toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });

const TimelineTooltip = ({ active, payload, series, metric, format }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0].payload;
  const values = metric === "revenue" ? entry.revenue : entry.units;
  const total = metric === "revenue" ? entry.totalRevenue : entry.totalUnits;
  const rows = series
    .map((item, index) => ({ ...item, index, value: values[item.key] || 0 }))
    .filter((item) => item.value > 0)
    .reverse(); // match the stack, top to bottom
  return (
    <Paper
      elevation={4}
      sx={{ px: 1.5, py: 1, borderRadius: 1.5, minWidth: 180, maxWidth: 280 }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
        {entry.label}
        {entry.partial ? " · part month" : ""}
      </Typography>
      {!entry.hasData ? (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          No analyzable days this month.
        </Typography>
      ) : (
        <>
          {rows.map((item) => (
            <Box
              key={item.key}
              sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12 }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "2px",
                  bgcolor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography noWrap sx={{ fontSize: 12, flex: 1, color: "text.secondary" }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
                {format(item.value)}
              </Typography>
            </Box>
          ))}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              borderTop: 1,
              borderColor: "divider",
              mt: 0.5,
              pt: 0.5,
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Total</Typography>
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
            >
              {format(total)}
              {metric === "units" ? " units" : ""}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

/**
 * "When were sales lost?" for a bulk run: stacked monthly bars of lost units
 * (or revenue, when every priced scope shares one currency) for the top SKUs,
 * with the rest folded into "Other". Built purely from the bulk response.
 */
const LostSalesBulkTimeline = ({ bulkResult, productLabelById }) => {
  const theme = useTheme();
  const { darkMode } = useDarkMode();
  const [metric, setMetric] = useState("units");
  const shared = bulkResult?.shared || {};

  const timeline = useMemo(
    () =>
      buildBulkLostSalesTimeline({
        rows: bulkResult?.results || [],
        startDate: shared.start_date,
        endDate: shared.end_date,
        productLabelById,
      }),
    [bulkResult, shared.start_date, shared.end_date, productLabelById],
  );

  if (!timeline || !timeline.series.length) return null;

  const mode = darkMode ? "dark" : "light";
  const series = timeline.series.map((item, index) => ({
    ...item,
    color:
      item.key === OTHER_SERIES_KEY
        ? OTHER_COLOR[mode]
        : SERIES_COLORS[mode][index % SERIES_COLORS[mode].length],
  }));
  const revenue = timeline.revenue;
  const activeMetric = metric === "revenue" && revenue.available ? "revenue" : "units";
  const format =
    activeMetric === "revenue"
      ? (value) => formatCurrencyAmount(value, revenue.currency)
      : formatUnits;
  const formatAxis =
    activeMetric === "revenue"
      ? (value) => formatCurrencyAmount(value, revenue.currency, { compact: true })
      : (value) => formatUnits(value);
  const chartData = timeline.months.map((entry) => ({
    ...entry,
    ...(activeMetric === "revenue" ? entry.revenue : entry.units),
  }));
  const peak = peakTimelineMonth(timeline, activeMetric);
  const partialMonths = timeline.months.filter((entry) => entry.partial);
  const emptyMonths = timeline.months.filter((entry) => !entry.hasData);
  const surface = theme.palette.background.paper;
  const topSeriesKey = series[series.length - 1].key;

  const notes = [
    `Based on ${timeline.analyzedCount.toLocaleString()} analyzed ${
      timeline.analyzedCount === 1 ? "scope" : "scopes"
    }.`,
    partialMonths.length
      ? `${partialMonths.map((entry) => entry.label).join(" and ")} ${
          partialMonths.length === 1 ? "is a part month" : "are part months"
        }.`
      : "",
    emptyMonths.length
      ? `${emptyMonths.length} month${emptyMonths.length === 1 ? "" : "s"} had no analyzable days.`
      : "",
    activeMetric === "revenue" && revenue.partial
      ? "Revenue excludes lost units without a usable price."
      : "",
  ].filter(Boolean);

  return (
    <Box data-testid="bulk-lost-sales-timeline">
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
          mb: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14 }}>
            {activeMetric === "revenue"
              ? `Estimated Lost Revenue by Month (${revenue.currency})`
              : "Lost Sales by Month"}
          </Typography>
          {peak && (
            <Typography
              data-testid="bulk-timeline-peak"
              sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}
            >
              Most lost in{" "}
              <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                {peak.label}
              </Box>
              : {format(peak.value)}
              {activeMetric === "units" ? " units" : ""} (
              {Math.round(peak.share * 100)}% of the period)
            </Typography>
          )}
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={activeMetric}
          onChange={(_event, value) => value && setMetric(value)}
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontSize: 12,
              px: 1.25,
              py: 0.25,
            },
          }}
        >
          <ToggleButton value="units">Units</ToggleButton>
          <ToggleButton
            value="revenue"
            disabled={!revenue.available}
            title={
              revenue.mixed
                ? "Revenue spans several currencies, which are never combined."
                : !revenue.available
                  ? "No priced lost units to chart."
                  : undefined
            }
          >
            Revenue
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {series.length > 1 && (
        <Box
          component="ul"
          aria-label="Chart legend"
          sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, listStyle: "none", m: 0, mb: 1, p: 0 }}
        >
          {series.map((item) => (
            <Box
              component="li"
              key={item.key}
              sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: item.color }} />
              <Typography
                noWrap
                title={item.label}
                sx={{ fontSize: 12, color: "text.secondary", maxWidth: 220 }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              width={activeMetric === "revenue" ? 56 : 44}
              tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
              tickFormatter={formatAxis}
            />
            <Tooltip
              cursor={{ fill: theme.palette.action.hover }}
              content={
                <TimelineTooltip
                  series={series}
                  metric={activeMetric}
                  format={format}
                />
              }
            />
            {series.map((item) => (
              <Bar
                key={item.key}
                dataKey={item.key}
                name={item.label}
                stackId="lost"
                fill={item.color}
                stroke={surface}
                strokeWidth={1}
                maxBarSize={48}
                radius={item.key === topSeriesKey ? [4, 4, 0, 0] : 0}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Paper>
      <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.75 }}>
        {notes.join(" ")}
      </Typography>
    </Box>
  );
};

export default LostSalesBulkTimeline;
